import React, { FC, useEffect, useState } from 'react';
import cx from 'classnames';
import { BranchesOutlined } from '@ant-design/icons';
import { Badge, Button, Dropdown, Skeleton, Tag } from 'antd';
import Avatar from '../../../../../../../../shared/Avatar';
import { format, isThisYear, isToday } from 'date-fns';
import AddComment from '../../AddComment';
import { Eye, MoreHorizontal } from 'lucide-react';
import styles from './styles.module.css';
import { MarkdownPreview } from '../../../../../../../components/RichTextDescription/components/MarkdownPreview';
import type { MarkdownMentionPreviewContext } from '../../../../../../../components/RichTextDescription/types';
import * as translations from './strings';
import { getLangKey } from '../../../../../../../models/Language';
import { User } from '../../../../../../../models/User';
import { useCommentsState } from '../../../../../../../../providers/Comments';
import { useProjectState } from '../../../../../../../../providers/Project';
import { useCurrentUserState } from '../../../../../../../../providers/CurrentUser';
import Reactions from './Reactions';

const strings = translations[getLangKey()];

type Props = {
  id: number;
  userId: number;
  commitUrl?: string;
  text: string;
  commentHtml?: string | null;
  createdAt: string;
  isPrivate: boolean;
  commenter?: User;
  readByUsers: number[] | null | undefined; // this can be null due to some legacy handling of our data in backbone
  reactions: string;
  hideAvatar?: boolean;
};

const determineHasUserReadComment = (
  readByUsers: number[] | null | undefined,
  userId: number,
  featureEnabled: boolean,
  createdAt: Date,
  featureEnabledDate: Date,
  userCreatedDate: Date
) => {
  // if there is no info then we optimistically assume the user has read the comment
  if (readByUsers === null || readByUsers === undefined) return true;
  if (!featureEnabled) return true;

  // If the comment was created before the feature went live then show as read
  if (createdAt < featureEnabledDate) return true;
  // If the comment was created before the user then show as read
  if (createdAt < userCreatedDate) return true;

  return readByUsers.includes(userId);
};

const Comment: FC<Props> = ({
  id,
  userId,
  commitUrl,
  text,
  commentHtml,
  createdAt,
  isPrivate,
  commenter,
  readByUsers,
  reactions,
  hideAvatar = false
}) => {
  const {
    canEditComment,
    currentUserId,
    users,
    usersNotOnProject,
    updateComment,
    deleteComment,
    isAdminView,
    getContainer,
    taskId,
    apiDomain,
    mentionableTasks,
    setActiveTask
  } = useCommentsState();

  const [editMode, setEditMode] = useState(false);
  const { currentUser } = useCurrentUserState();
  const { project } = useProjectState();

  if (!commenter) return null;

  const unreadCommentsEnabled = true;

  const getEditor = () => {
    return (
      <AddComment
        initialValue={text}
        commentId={id}
        onCancel={() => setEditMode(false)}
        isPrivate={isPrivate}
      />
    );
  };

  const renderDate = () => {
    const date = new Date(createdAt);
    let displayFormat = "MMM d, yyyy 'at' h:mma";

    if (isThisYear(date)) {
      if (isToday(date)) {
        displayFormat = "'Today' 'at' h:mma";
      } else {
        displayFormat = "MMM d 'at' h:mma";
      }
    }

    return <span className={styles.date}>{format(date, displayFormat)}</span>;
  };

  const hasEditCommentPermission: boolean =
    canEditComment && userId === currentUserId;

  const mentionPreviewContext: MarkdownMentionPreviewContext = {
    descriptionMarkdown: text,
    projectMembers: users,
    allUsers: users.concat(usersNotOnProject?.length ? usersNotOnProject : []),
    mentionableTasks,
    setActiveTask,
    isComment: true,
    isPrivate
  };

  const userReadComment = determineHasUserReadComment(
    readByUsers,
    Number(currentUserId),
    unreadCommentsEnabled,
    new Date(createdAt),
    new Date(project?.unreadCommentsEnabledDate || 0),
    new Date(currentUser?.createdAt || 0)
  );

  const [hasUserReadComment, setHasUserReadComment] = useState<boolean>(
    userReadComment
  );

  useEffect(() => {
    setHasUserReadComment(userReadComment);
  }, [userReadComment]);
  const markAsRead = () => {
    if (hasUserReadComment) return;

    updateComment({
      commentId: id,
      commentEvent: {
        type: 'read'
      },
      taskId
    });
    setHasUserReadComment(true);
  };

  const renderReactions = (
    showReactions: boolean,
    showPicker: boolean,
    pickerPlacement: 'leftBottom' | 'bottomRight' = 'leftBottom'
  ) => (
    <Reactions
      id={id}
      currentUser={currentUser}
      getContainer={getContainer}
      reactions={reactions}
      isPrivate={isPrivate}
      users={[
        ...users,
        ...(usersNotOnProject?.length
          ? usersNotOnProject
          : []
        ).filter(({ role }) => role?.includes('manager'))
      ]}
      updateComment={updateComment}
      taskId={taskId}
      apiDomain={apiDomain}
      isAdminView={!!isAdminView}
      projectId={project?.id || 0}
      showReactions={showReactions}
      showPicker={showPicker}
      pickerPlacement={pickerPlacement}
    />
  );

  if (id === -1) {
    return (
      <div className={cx(styles.container)}>
        <Skeleton active avatar paragraph={{ rows: 1 }} />
      </div>
    );
  }

  return (
    <Badge dot={!hasUserReadComment} className={styles.antdBadgeOverride}>
      <div
        className={cx(styles.container, {
          [styles.privateComment]: isPrivate && !editMode,
          [styles.unreadComment]: !hasUserReadComment,
          [styles.adminContainer]: isAdminView
        })}
        onMouseOver={markAsRead}
        data-cy="comment-item"
      >
        <div className={styles.commentContent}>
          {!hideAvatar && (
            <Avatar className={styles.avatar} member={commenter} size="small" />
          )}
          <div className={styles.bodyContainer}>
            <div className={styles.header}>
              <div>
                <b>{commenter.name}</b>
                {renderDate()}
              </div>
              <div className={styles.headerActions}>
                {renderReactions(false, true, 'bottomRight')}
                {hasEditCommentPermission && (
                  <Dropdown
                    trigger={['click']}
                    placement="bottomRight"
                    menu={{
                      items: [
                        { key: 'edit', label: strings.edit },
                        {
                          key: 'delete',
                          label: strings.delete,
                          danger: true
                        }
                      ],
                      onClick: ({ key }) => {
                        if (key === 'edit') {
                          setEditMode(true);
                          return;
                        }

                        deleteComment(id);
                      }
                    }}
                    getPopupContainer={getContainer}
                  >
                    <Button
                      type="text"
                      size="small"
                      className={styles.moreButton}
                      icon={<MoreHorizontal size={16} />}
                      aria-label="Comment actions"
                      data-cy="comment-edit-button"
                    />
                  </Dropdown>
                )}
              </div>
            </div>
            {editMode ? (
              getEditor()
            ) : (
              <div className={styles.body} data-cy="comment-body">
                <MarkdownPreview
                  content={text}
                  htmlContent={commentHtml}
                  interactive
                  className={cx(styles.commentText, {
                    [styles.privateCommentText]: isPrivate
                  })}
                  mentionPreviewContext={mentionPreviewContext}
                  imagePreviewContainer={getContainer}
                />
                {commitUrl && (
                  <div className={styles.actions}>
                    <Tag>
                      <a
                        rel="noreferrer noopener"
                        target="_blank"
                        href={commitUrl}
                        className={styles.externalCommitLink}
                      >
                        <BranchesOutlined />
                        <span className={styles.externalCommitText}>
                          {strings.viewCommit}
                        </span>
                      </a>
                    </Tag>
                  </div>
                )}
              </div>
            )}
            {!editMode && isPrivate && (
              <div className={styles.privateCommentFooter}>
                <Eye className={styles.eyeIcon} />
                <span className={styles.visibleTo}>{strings.membersOnly}</span>
              </div>
            )}
          </div>
        </div>
        {!editMode && renderReactions(true, false)}
      </div>
    </Badge>
  );
};

export default Comment;
